import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

const badge = cva(
  [
    'num inline-flex items-center justify-center',
    'min-w-5 rounded-chip px-1 py-px',
    'text-chip tabular-nums',
  ],
  {
    variants: {
      tone: {
        danger: 'bg-status-danger text-status-danger-fg',
        warning: 'bg-status-warning text-status-warning-fg',
        neutral: 'bg-status-neutral text-status-neutral-fg',
        info: 'bg-status-info text-status-info-fg',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

export type BadgeVariants = VariantProps<typeof badge>;

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'>, BadgeVariants {
  /** The count. Zero renders nothing — an empty queue is not a badge. */
  readonly count: number;
  /**
   * What the number counts, already translated and already pluralised:
   * "3 exceptions bloquantes". A bare "3" beside an icon tells a screen-reader
   * user nothing at all.
   */
  readonly label: string;
  /** Counts above this render as "N+". */
  readonly max?: number;
}

/**
 * A count on a nav item or a tab.
 *
 * **`info` is the default, not `danger` (owner request, 2026-08-31).** Almost
 * every count in this product is inventory — four employees with access, two
 * zones, three sites — and a red chip on each of them said « something is
 * wrong here » about a page where nothing is. Red is now opt-in, and the
 * whole product spends it in one place: the open high-severity exceptions in
 * the sidebar, which is the only count that means *act now*.
 *
 * The blue is the sanctioned one. `--color-status-info` is brand blue used as
 * a **solid field under near-black text** (7.39:1) — the single exception the
 * Identity-Never-Means rule carves out — never blue text and never a blue
 * border. It reads as a quantity, which is what it is.
 *
 * A number is the only place in this system where a colour carries meaning
 * without a word beside it, so the word lives in the accessible name and the
 * count never appears alone in the tree.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { count, label, max = 99, tone, className, ...rest },
  ref,
) {
  if (count <= 0) return null;

  return (
    <span {...rest} ref={ref} className={cn(badge({ tone }), className)}>
      <span aria-hidden="true">{count > max ? `${max}+` : count}</span>
      <VisuallyHidden>{label}</VisuallyHidden>
    </span>
  );
});
